import type { LocalStore } from '@ims/local'
import type { SupabaseClient } from '@ims/remote'

// What the host (apps/web today, Electron later) provides to the app. The app
// itself never touches workers, OPFS, locks or service workers directly.

export interface OpenedStore {
  store: LocalStore
  /** Stops sync, closes the database and releases the tab lock. */
  release(): Promise<void>
}

export interface StorageStatus {
  persisted: boolean
  usageBytes: number | null
  quotaBytes: number | null
}

export interface AppInstall {
  /** The browser can show its install prompt now (Chrome, Edge, Android). */
  available(): boolean
  /** Running as the installed app. */
  installed(): boolean
  /** How to install where there is no prompt (iOS Safari), or null. */
  manualHint(): string | null
  prompt(): Promise<'accepted' | 'dismissed' | 'unavailable'>
  /** Called when availability or the installed state changes. */
  subscribe(listener: () => void): () => void
}

export interface OpenStoreOptions {
  /** Ask the tab that holds the database to hand it over ("Use here"). */
  takeOver?: boolean
  /** Another tab wants the database: close it (the app shows a notice). */
  onTakeoverRequested: () => void
}

export interface AppPlatform {
  supabase: SupabaseClient
  /** Base URL of the api Worker (invites, share links, images). */
  apiUrl: string
  /** Base URL of the guests' menu (table QR codes): menu.sanogodam.com. */
  menuUrl: string
  /** Stable id of this browser install (billing counters are bound to it). */
  installationId: string
  /** Throws TabLockedError when another tab has this tenant open. */
  openStore(userId: string, tenantId: string, options: OpenStoreOptions): Promise<OpenedStore>
  /** Deletes a tenant's local database (logout on shared computers). */
  deleteLocalData(userId: string, tenantId: string): Promise<void>
  /** Sync on reconnect / tab focus. Returns a detach function. */
  attachSyncTriggers(trigger: () => void): () => void
  requestPersistentStorage(): Promise<StorageStatus>
  /** Installing the app (it also makes browsers keep its offline data). */
  install: AppInstall
  /** Excel/CSV reading and writing (off the main thread). */
  spreadsheets: SpreadsheetIO
  /** Sound and the app icon's count for alerts (restaurant mode). */
  alerts: AppAlerts
  /** Restaurant alerts while the app is closed (Web Push); absent where there is none. */
  push?: AppPush
  /** Barcodes read with the device's camera. */
  scanner: BarcodeScanner
  /** A very short buzz under the finger, where the device has one (a tap on the menu). */
  haptics?: { tap(): void }
}

export interface BarcodeScanner {
  /** False where the browser has no camera API (then no scan button is shown). */
  available(): boolean
  /**
   * Opens the back camera on `video` and reports every code it reads, several times a
   * second while one stays in view (the caller ignores repeats). `region` is the guide
   * box, as fractions of the picture the person sees: that part is read at the camera's
   * full resolution. Rejects with the browser's error when the camera is refused
   * (`NotAllowedError`) or missing.
   */
  start(
    video: HTMLVideoElement,
    onCode: (code: string) => void,
    options?: { region?: { x: number; y: number; w: number; h: number } },
  ): Promise<ScanSession>
  /**
   * The text of a QR code in a picture someone chose (a photo of their payment QR), or
   * null when none is found. Rejects when the file is not a picture the browser can open.
   */
  readImage(file: Blob): Promise<string | null>
  /** A short beep and buzz for a code that was accepted. */
  confirm(): void
  /** A double buzz and no beep for a code no product has: it must not feel like a sale. */
  miss(): void
}

/** What the scanner is working with, for the diagnostics line. */
export interface ScanInfo {
  /** The phone's own barcode reader, or the bundled WebAssembly one. */
  reader: 'built-in' | 'wasm'
  /** The picture the camera delivers, in pixels. */
  width: number
  height: number
  zoom: number | null
  camera: string
  /** The lens can refocus (a fixed-focus lens cannot read a code held close). */
  autofocus: boolean
  /** The phone's light works with this lens. */
  light: boolean
}

/** A running camera. Every control is present only where the device offers it. */
export interface ScanSession {
  /** Turns the camera off. */
  stop(): void
  /** Switches the phone's light. */
  torch?: (on: boolean) => Promise<void>
  zoom?: {
    min: number
    max: number
    value(): number
    set(level: number): Promise<void>
  }
  /** Focuses on a point, given as fractions (0 to 1) of the picture the person sees. */
  focusAt?: (x: number, y: number) => Promise<void>
  /** More than one back camera: moves to the next and remembers it for this device. */
  nextCamera?: () => Promise<void>
  info(): ScanInfo
}

export interface AppAlerts {
  /** A short chime. Silent until the person has tapped the page (browsers require it). */
  chime(): void
  /** The number on the installed app's icon, where the device has one; 0 clears it. */
  setBadge(count: number): void
}

/** A browser's push subscription: where to send, and its keys (base64url). */
export interface PushDevice {
  endpoint: string
  p256dh: string
  auth: string
}

/** Web Push on this device (docs/plans/push-alerts.md). */
export interface AppPush {
  /** The server's VAPID public key; null where push is not set up (then it is hidden). */
  publicKey: string | null
  /** Whether this browser can get alerts while closed at all. */
  supported(): boolean
  /** An iPhone or iPad in a browser tab: push needs the app added to the Home Screen. */
  needsInstall(): boolean
  /** The notification permission: 'default' (not asked), 'granted' or 'denied'. */
  permission(): NotificationPermission
  /** Asks for permission (call it from a tap) and subscribes; null when it is refused. */
  subscribe(): Promise<PushDevice | null>
  /** This browser's subscription, if it has one. */
  current(): Promise<PushDevice | null>
  /** Ends this browser's subscription; its endpoint, to forget on the server. */
  unsubscribe(): Promise<string | null>
}

export interface SpreadsheetSheet {
  name: string
  /** Cells as display text, so codes keep their leading zeros. */
  rows: string[][]
  /** The sheet had more rows than the reader keeps (200,000). */
  truncated?: boolean
}

export interface SpreadsheetIO {
  /** Reads .xlsx, .xls or .csv. Each sheet is capped at 200,000 rows. */
  read(data: ArrayBuffer): Promise<SpreadsheetSheet[]>
  /** Writes an .xlsx workbook. */
  write(sheets: { name: string; rows: (string | number)[][] }[]): Promise<Blob>
}

export class TabLockedError extends Error {
  constructor() {
    super('This business is open in another tab')
    this.name = 'TabLockedError'
  }
}
